/**
 * SessionActions
 */

import { Injectable } from '@angular/core';
import { NgRedux } from '@angular-redux/store';
import { IAppState } from '@store/reducers';
import { IAction } from '@shared/types';
import { ILocales } from '@app/i18n/i18n.types';

/**
 * # SessionActions
 * Defines the class for creating user session actions
 */

@Injectable()
export class SessionActions {
  public static FETCH_ACCESS_TOKEN: string = 'FETCH_ACCESS_TOKEN';
  public static FETCH_ACCESS_TOKEN_SUCCESS: string =
    'FETCH_ACCESS_TOKEN_SUCCESS';
  public static FETCH_ACCESS_TOKEN_ERROR: string = 'FETCH_ACCESS_TOKEN_ERROR';
  public static FETCH_PERSONAL_TOKEN: string = 'FETCH_PERSONAL_TOKEN';
  public static FETCH_PERSONAL_TOKEN_SUCCESS: string =
    'FETCH_PERSONAL_TOKEN_SUCCESS';
  public static FETCH_PERSONAL_TOKEN_ERROR: string =
    'FETCH_PERSONAL_TOKEN_ERROR';
  public static FETCH_USER_INFO: string = 'FETCH_USER_INFO';
  public static FETCH_USER_INFO_SUCCESS: string = 'FETCH_USER_INFO_SUCCESS';
  public static FETCH_USER_INFO_ERROR: string = 'FETCH_USER_INFO_ERROR';
  public static LOGOUT_USER: string = 'LOGOUT_USER';
  public static RESTORE_TOKENS: string = 'RESTORE_TOKENS';
  public static RESTORE_TOKENS_SUCCESS: string = 'RESTORE_TOKENS_SUCCESS';
  public static RESTORE_ACCESS_TOKEN: string = 'RESTORE_ACCESS_TOKEN';
  public static RESTORE_PERSONAL_TOKEN: string = 'RESTORE_PERSONAL_TOKEN';
  public static RESTORE_PERSONAL_TOKEN_SUCCESS: string =
    'RESTORE_PERSONAL_TOKEN_SUCCESS';
  public static SET_IFRAME_MODE: string = 'SET_IFRAME_MODE';
  public static TOKEN_REMOVED: string = 'TOKEN_REMOVED';

  public static FETCH_PERMISSIONS: string = 'FETCH_PERMISSIONS';
  public static FETCH_PERMISSIONS_SUCCESS: string = 'FETCH_PERMISSIONS_SUCCESS';
  public static FETCH_PERMISSIONS_ERROR: string = 'FETCH_PERMISSIONS_ERROR';

  public static FETCH_PREFERENCES_FOR_COMPANY: string =
    'FETCH_PREFERENCES_FOR_COMPANY';

  public static FETCH_TRANSLATIONS: string = 'FETCH_TRANSLATIONS';

  public static SAVE_LOCALES: string = 'SAVE_LOCALES';

  public static SET_TRANSLATION_INITIALIZED: string =
    'SET_TRANSLATION_INITIALIZED';

  public static logoutUserAction(): IAction {
    return {
      type: SessionActions.LOGOUT_USER,
    };
  }

  public static fetchAccessTokenAction(companyId: string): IAction {
    return {
      type: SessionActions.FETCH_ACCESS_TOKEN,
      payload: {
        companyId,
      },
    };
  }
  public static fetchAccessTokenSuccessAction(token: string): IAction {
    return {
      type: SessionActions.FETCH_ACCESS_TOKEN_SUCCESS,
      payload: token,
    };
  }
  public static fetchAccessTokenErrorAction(errors): IAction {
    return {
      type: SessionActions.FETCH_ACCESS_TOKEN_ERROR,
      payload: errors,
    };
  }

  public static fetchPersonalTokenAction(credentials): IAction {
    return {
      type: SessionActions.FETCH_PERSONAL_TOKEN,
      payload: credentials,
    };
  }
  public static fetchPersonalTokenSuccessAction(token: string): IAction {
    return {
      type: SessionActions.FETCH_PERSONAL_TOKEN_SUCCESS,
      payload: token,
    };
  }
  public static fetchPersonalTokenErrorAction(errors): IAction {
    return {
      type: SessionActions.FETCH_PERSONAL_TOKEN_ERROR,
      payload: errors,
    };
  }

  public static fetchUserInfoAction(companyId: string): IAction {
    return {
      type: SessionActions.FETCH_USER_INFO,
      payload: {
        companyId,
      },
    };
  }
  public static fetchUserInfoSuccessAction(user): IAction {
    return {
      type: SessionActions.FETCH_USER_INFO_SUCCESS,
      payload: { user },
    };
  }
  public static fetchUserInfoErrorAction(errors): IAction {
    return {
      type: SessionActions.FETCH_USER_INFO_ERROR,
      payload: errors,
    };
  }

  public static setIframeModeAction(isInIframe: boolean): IAction {
    return {
      type: SessionActions.SET_IFRAME_MODE,
      payload: isInIframe,
    };
  }

  /**
   * TOKEN  ACTIONS
   */
  public static restoreTokensAction(): IAction {
    return {
      type: SessionActions.RESTORE_TOKENS,
    };
  }

  public static restoreTokensSuccessAction(tokensObj): IAction {
    return {
      type: SessionActions.RESTORE_TOKENS_SUCCESS,
      payload: {
        accessToken: tokensObj.accessToken,
        personalToken: tokensObj.personalToken,
      },
    };
  }

  public static restoreAccessTokenAction(): IAction {
    return {
      type: SessionActions.RESTORE_ACCESS_TOKEN,
    };
  }

  public static restorePersonalTokenAction(): IAction {
    return {
      type: SessionActions.RESTORE_PERSONAL_TOKEN,
    };
  }

  public static restorePersonalTokenSuccessAction(token: string): IAction {
    return {
      type: SessionActions.RESTORE_PERSONAL_TOKEN_SUCCESS,
      payload: token,
    };
  }

  public static tokenRemovedAction(): IAction {
    return {
      type: SessionActions.TOKEN_REMOVED,
    };
  }

  /**
   * User permissions fetch
   */

  public static fetchPermissionsAction(query?: string[]): IAction {
    return {
      type: SessionActions.FETCH_PERMISSIONS,
      payload: {
        query,
      },
    };
  }

  public static fetchPermissionsSuccessAction(servicePayload): IAction {
    return {
      type: SessionActions.FETCH_PERMISSIONS_SUCCESS,
      payload: servicePayload,
    };
  }

  public static fetchPermissionsErrorAction(errors): IAction {
    return {
      type: SessionActions.FETCH_PERMISSIONS_ERROR,
      payload: {
        errors,
      },
    };
  }

  public static saveLocalesAction(locales: ILocales): IAction {
    return {
      type: SessionActions.SAVE_LOCALES,
      payload: {
        locales,
      },
    };
  }

  public static fetchTranslationsAction(): IAction {
    return {
      type: SessionActions.FETCH_TRANSLATIONS,
      // need this to keep ui reducer happy
      payload: {},
    };
  }

  public static setTranslationInitializedAction(
    isInitialized: boolean,
  ): IAction {
    return {
      type: SessionActions.SET_TRANSLATION_INITIALIZED,
      payload: {
        isInitialized,
      },
    };
  }

  /**
   * Initialize the instance
   */
  public constructor(private ngRedux: NgRedux<IAppState>) {}

  /**
   * Dispatch action for doing request
   *
   */

  public fetchPersonalToken(credentials): void {
    this.ngRedux.dispatch(SessionActions.fetchPersonalTokenAction(credentials));
  }

  public setActiveCompany(companyId: string): void {
    this.getAccessToken(companyId);
  }
  public getAccessToken(companyId: string): void {
    this.ngRedux.dispatch(SessionActions.fetchAccessTokenAction(companyId));
  }

  public restoreAccessToken(): void {
    this.ngRedux.dispatch(SessionActions.restoreAccessTokenAction());
  }

  public restorePersonalToken(): void {
    this.ngRedux.dispatch(SessionActions.restorePersonalTokenAction());
  }

  public restoreTokens(): void {
    this.ngRedux.dispatch(SessionActions.restoreTokensAction());
  }

  public setIframeMode(isInIframe: boolean): void {
    this.ngRedux.dispatch(SessionActions.setIframeModeAction(isInIframe));
  }

  public logoutUser(): void {
    this.ngRedux.dispatch(SessionActions.logoutUserAction());
  }

  public fetchPermissions(query?: any): void {
    this.ngRedux.dispatch(SessionActions.fetchPermissionsAction(query));
  }

  public saveSessionLocales(locales: ILocales): void {
    this.ngRedux.dispatch(SessionActions.saveLocalesAction(locales));
  }

  public fetchTranslations(): void {
    this.ngRedux.dispatch(SessionActions.fetchTranslationsAction());
  }

  public setTranslationInitialized(isInitialized: boolean): void {
    this.ngRedux.dispatch(
      SessionActions.setTranslationInitializedAction(isInitialized),
    );
  }
}
